import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TransactionB2C } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { TransactionsService } from '@/services/transactionsService';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  DollarSign,
  FileText, 
  Loader2, 
  User,
  Clock,
  Building,
  Hash,
  Copy,
  Mail,
  ShoppingCart,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import PageHeader from "@/components/common/PageHeader";
import { StatusBadge } from '@/components/common/StatusBadge';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TransactionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<TransactionB2C | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchTransactionDetails(id);
    }
  }, [id]);

  const fetchTransactionDetails = async (transactionId: string) => {
    setLoading(true);
    try {
      const data = await TransactionsService.getTransactionById(transactionId);
      
      if (!data) {
        toast.error('Transação não encontrada');
        navigate('/transactions');
        return;
      }
      
      setTransaction(data);
    } catch (error) {
      console.error('Erro ao buscar detalhes da transação:', error);
      toast.error('Falha ao carregar detalhes da transação: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado para a área de transferência`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'destructive';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Concluído';
      case 'pending':
        return 'Pendente';
      case 'failed':
        return 'Falhou';
      case 'refunded':
        return 'Reembolsado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando detalhes da transação...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Transação não encontrada</h2>
          <Button onClick={() => navigate('/transactions')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Transações
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Detalhes da Transação {transaction.display_id}</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Hero Header */}
        <div 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/transactions')}
                  className="text-white hover:text-white/80 hover:bg-white/10 -ml-2 mb-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar para Transações
                </Button>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white flex items-center gap-2">
                      Transação {transaction.display_id}
                      <Badge variant={getStatusColor(transaction.status)}>
                        {getStatusLabel(transaction.status)}
                      </Badge>
                    </h1>
                    <p className="text-blue-100 text-base pb-2">
                      Detalhes completos da transação
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Transaction Details */}
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <CardTitle>Informações da Transação</CardTitle>
                  </div>
                  <CardDescription>Detalhes principais da transação</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Hash className="h-4 w-4" />
                        <span className="font-extrabold">ID de Exibição</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-sm">{transaction.display_id}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(transaction.display_id, 'ID')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <DollarSign className="h-4 w-4" />
                        <span className="font-extrabold">Valor</span>
                      </div>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(Number(transaction.price))}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span className="font-extrabold">Data da Venda</span>
                      </div>
                      <p className="text-sm">
                        {format(new Date(transaction.sale_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span className="font-extrabold">Status</span>
                      </div>
                      <Badge variant={getStatusColor(transaction.status)}>
                        {getStatusLabel(transaction.status)}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CreditCard className="h-4 w-4" />
                        <span className="font-extrabold">Método de Pagamento</span>
                      </div>
                      <p className="text-sm">{transaction.payment_method || '-'}</p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building className="h-4 w-4" />
                        <span className="font-extrabold">Gateway de Pagamento</span>
                      </div>
                      <p className="text-sm">{transaction.payment_gateway || '-'}</p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ShoppingCart className="h-4 w-4" />
                        <span className="font-extrabold">Origem</span>
                      </div>
                      <p className="text-sm">{transaction.source || '-'}</p>
                    </div>

                    {transaction.installments && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CreditCard className="h-4 w-4" />
                          <span className="font-extrabold">Parcelas</span>
                        </div>
                        <p className="text-sm">{transaction.installments}x</p>
                      </div>
                    )}

                    {transaction.external_transaction_id && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ExternalLink className="h-4 w-4" />
                          <span className="font-extrabold">ID Externo</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-mono truncate">{transaction.external_transaction_id}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(transaction.external_transaction_id!, 'ID Externo')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {transaction.notes && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="h-4 w-4" />
                          <span className="font-extrabold">Observações</span>
                        </div>
                        <p className="text-sm bg-muted/50 p-3 rounded-md">{transaction.notes}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Product Information */}
              {transaction.product && Object.keys(transaction.product).length > 0 && (
                <Card className="border-0 shadow-xl">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5 text-primary" />
                      <CardTitle>Informações do Produto</CardTitle>
                    </div>
                    <CardDescription>Detalhes do produto vendido</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted/50 p-4 rounded-md text-xs overflow-auto">
                      {JSON.stringify(transaction.product, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Customer Information */}
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    <CardTitle>Cliente</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(transaction as any).customer_contacts ? (
                    <>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Nome</p>
                        <p className="font-extrabold">
                          {`${(transaction as any).customer_contacts.first_name} ${(transaction as any).customer_contacts.last_name || ''}`.trim()}
                        </p>
                      </div>
                      {(transaction as any).customer_contacts.email && (
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            Email
                          </p>
                          <p className="text-sm truncate">{(transaction as any).customer_contacts.email}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum cliente associado</p>
                  )}
                </CardContent>
              </Card>

              {/* Metadata */}
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-base">Metadados do Sistema</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">ID do Sistema</p>
                    <p className="text-xs font-mono bg-muted/50 p-2 rounded truncate">{transaction.id}</p>
                  </div>
                  {transaction.created_at && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Criado em</p>
                      <p className="text-xs">
                        {format(new Date(transaction.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  )}
                  {transaction.updated_at && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Atualizado em</p>
                      <p className="text-xs">
                        {format(new Date(transaction.updated_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionDetail;
